<template>
    <div>
        <div class="row justify-end" v-if="test_status == 'not_started'">
            <div class="col-12" v-if="activity.type_activity == 'test'">
                <p class="q-title">{{ `${$t('test_type')}: ${$t(activity.type_slug)}` }}</p>
            </div>
            <div class="col-12" v-if="activity.type_activity == 'quiz'">
                <p class="q-title">{{ `${$t('quiz_type')}: ${$t(activity.type_slug)}` }}</p>
            </div>

            <div class="col-12" v-if="activity.type_activity == 'test'">
                <p>{{ $t(`description_${activity.type_slug}`) }}</p>

                <p>
                    {{ $t('test_you_need_to_score') }}
                    <strong>{{ activity.minimum_score }}</strong> {{ $t('of') }}
                    <strong>{{ activity.qty_questions }}</strong>
                    {{ $t('test_questions_to_be_approved_in_this_test') }}
                </p>

                <p v-if="activity.type_slug == 'block_test' || activity.type_slug == 'restart_test'">
                    {{ $t('test_you_have_tried') }}
                    <strong>{{ activity.qty_attempted }}</strong> {{ $t('of') }}
                    <strong>{{ activity.maximum_attempts }}</strong>
                    {{ $t('test_possible_tries') }}
                </p>
            </div>

            <div class="col-12" v-if="activity.type_activity == 'quiz'">
                <p v-if="activity.type_slug == 'evaluated_quiz'">
                    {{ $t(`description_${activity.type_slug}`) }} <strong>{{ activity.minimum_score }}</strong> {{ $t(`description_2_${activity.type_slug}`) }}
                </p>

                <p v-if="activity.type_slug == 'unevaluated_quiz'">{{ $t(`description_${activity.type_slug}`) }}</p>
            </div>

            <div class="col-12" v-if="showDanger()">
                <q-alert color="negative" icon="fas fa-ban">
                    {{ $t(`test_start_danger_${activity.type_slug}`) }}
                </q-alert>
            </div>

            <div class="col-12" v-if="realizations.length > 0">
                <q-list dense>
                    <q-collapsible icon="fas fa-pen" :label="$t('previous_results')">
                        <q-table
                            ref="table_realizations"
                            :pagination.sync="server_pagination"
                            :data="realizations"
                            :columns="columns"
                            row-key="id"
                            :no-data-label="$t('no_data_avaliable')"
                            :no-results-label="$t('no_results_found')"
                            :loading-label="$t('loading_data')"
                            :rows-per-page-label="$t('items_per_page')"
                            :rows-per-page-options="[0]"
                            hide-bottom
                            class="shadow-0"
                            >

                            <q-tr slot="header" slot-scope="props" :props="props">
                                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                                {{ $t(col.label) }}
                                </q-th>
                            </q-tr>

                            <template slot="body" slot-scope="props">
                                <q-tr :props="props" @click.native="showModalResult(props.row)">
                                    <q-td key="date" :props="props">
                                        {{ format(props.row.date) }}
                                    </q-td>

                                    <q-td key="percent" :props="props">
                                        {{ props.row.percent.toFixed(2) }}%
                                    </q-td>

                                    <q-td key="score" :props="props">
                                        {{ props.row.score.toFixed(2) }}
                                    </q-td>

                                    <q-td key="test_score" :props="props">
                                        {{ props.row.test_score > 0 ? props.row.test_score.toFixed(2) : '' }}
                                    </q-td>

                                    <q-td key="status" :props="props">
                                        <span :class="getResultClass(props.row.status)">{{ $t(getResult(props.row.status)) }}</span>
                                    </q-td>
                                </q-tr>
                            </template>
                        </q-table>
                    </q-collapsible>
                </q-list>
            </div>

            <div class="col-xs-12 col-sm-3 q-mt-md">
                <q-btn color="secondary" :label="$t('start')" icon="fas fa-play" class="full-width" @click="startTest()" :disable="activity.type_slug === 'unevaluated_quiz' ? !activity.allowed_to_answer : activity.allowed_to_answer && hasHighestScore == false ? false : true" />
            </div>
        </div>

        <div class="row gutter-sm justify-end question-list" v-if="test_status == 'started'">
            <div class="col-xs-12 col-sm-12" v-for="(question, index) in questions" :key="index">
                <span :class="getQuestionClass(index, question)">
                    {{ index + 1 }}) <span v-html="question.title"></span>
                </span>

                <q-field :error="question.error">
                    <q-option-group
                        type="radio"
                        color="secondary"
                        v-model="question.answer"
                        :options="question.answers"
                        class="questions q-mt-xs"
                        @input="question.error = false"
                    />
                </q-field>
            </div>

            <div class="col-xs-12 col-sm-3">
                <q-btn color="secondary" :label="$t('answer')" icon="far fa-save" class="full-width" @click="finishTest()" />
            </div>
        </div>

        <div class="row gutter-sm justify-end question-list" v-if="test_status == 'finished'">
            <div class="col-12" v-if="activity.type_activity == 'test'">
                <p class="q-title">{{ $t('test_finished') }}</p>
                <p>{{ $t('you_finished_the_test_and_your_result_is') }}:
                    <span :class="getResultClass(result.status)"><strong>{{ $t(getResult(result.status)) }}</strong></span>
                </p>
                <p>
                    {{ `${$t('your_score_was')} ${result.score} ${$t('of')} ${result.qty_questions}, ${$t('a_total_of')} ${result.percent.toFixed(0)}% ${$t('of_right_answers')}.` }}
                </p>
                <p v-if="result.maximum_attempts">
                    {{ $t('this_was_your_attempt') }} <strong>{{ result.qty_attempted }}</strong> {{ $t('of') }} <strong>{{ result.maximum_attempts }}</strong>.
                </p>
            </div>

            <div class="col-12" v-if="activity.type_activity == 'quiz'">
                <p class="q-title">{{ $t('quiz_finished') }}</p>
                <p v-if="activity.type_slug == 'evaluated_quiz'">{{ $t('you_finished_the_quiz_and_your_result_is') }}:
                    <span :class="getResultClass(result.status)"><strong>{{ $t(getResult(result.status)) }}</strong></span>
                </p>
                <p>
                    {{ `${$t('your_score_was')} ${result.score} ${$t('of')} ${result.qty_questions}, ${$t('a_total_of')} ${result.percent.toFixed(0)}% ${$t('of_right_answers')}.` }}
                </p>
                <p v-if="result.maximum_attempts">
                    {{ $t('this_was_your_attempt') }} <strong>{{ result.qty_attempted }}</strong> {{ $t('of') }} <strong>{{ result.maximum_attempts }}</strong>.
                </p>
            </div>

            <div class="col-xs-12 col-sm-3">
                <q-btn color="secondary" :label="$t('try_again')" icon="fas fa-sync" class="full-width" @click="tryAgain()" v-if="show_try_again && activity.allowed_to_answer && result.percent != 100" :disable="!selected_client_params.group_training_test_improve_score" />
            </div>

            <div class="col-xs-12 col-sm-3">
                <q-btn color="tertiary" :label="$t('see_result')" icon="far fa-eye" class="full-width" @click="showModalResult(result)" />
            </div>

            <div class="col-xs-12 col-sm-3">
                <q-btn color="primary" :label="$t('close')" icon="fas fa-times" class="full-width" @click="closeTest()" />
            </div>
        </div>

        <q-modal v-model="show_modal" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '90vh'}">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="show_modal = false" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('result') }}: {{ activity.type_title }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="row gutter-sm justify-end question-list">
                        <div class="col-xs-12 col-sm-12" v-for="(question, index) in realization_questions" :key="index">
                            <p class="q-subheading text-weight-medium q-mb-xs" :class="question.is_correct ? 'text-positive' : 'text-negative'" :id="'qt' + question.id">
                                {{ index + 1 }}) <span v-html="question.title"></span>
                            </p>

                            <q-field :error="question.error">
                                <q-option-group
                                    type="radio"
                                    v-model="question.answer"
                                    :options="question.answers"
                                    class="questions q-mt-xs"
                                    :class="question.is_correct? 'checkbox-hit' : 'checkbox-error'"
                                    @input="question.error = false"
                                    readonly
                                />
                            </q-field>
                        </div>
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
export default {
    props: ['activity', 'closing'],
    data() {
        return {
            id_course: '',
            questions: [],
            test_status: 'not_started',
            result: [],
            realizations: [],
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: 0,
                descending: false,
                sortBy: 'order'
            },
            columns: [
                {
                    name: 'date',
                    required: true,
                    label: 'date',
                    align: 'left',
                    field: 'date',
                    sortable: false,
                },
                {
                    name: 'percent',
                    required: true,
                    label: 'percent',
                    align: 'left',
                    field: 'percent',
                    sortable: false,
                },
                {
                    name: 'score',
                    required: true,
                    label: 'hits',
                    align: 'left',
                    field: 'score',
                    sortable: false,
                },
                {
                    name: 'test_score',
                    required: true,
                    label: 'score',
                    align: 'left',
                    field: 'test_score',
                    sortable: false,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'status',
                    align: 'left',
                    field: 'status',
                    sortable: false,
                }
            ],
            show_modal: false,
            realization_questions: [],
            show_try_again: false,
            selected_client_params: []
        }
    },
    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected'
        }),
        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },
        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '90vw'
        },
        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        },

        hasHighestScore() {
            let success = false
            this.realizations.map(e => {
                if(e.percent == 100) success = true
            })
            return success

        }

    },
    methods: {
        startTest() {
            this.test_status = 'started'
            this.getQuestions()
        },
        getQuestions() {
            this.$q.loading.show()
            this.questions = []
            this.$axios.get(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/`, this.getHeader)
            .then(response => {
                response.data.group_training_test_questions.forEach(question => {
                    let answers = []
                    question.group_training_test_question_answers.forEach(answer => {
                        answers.push({ label: answer.title, value: answer.id })
                    })
                    this.questions.push({
                        id: question.id,
                        title: question.title,
                        answers: answers,
                        answer: '',
                        error: false
                    })
                })
                this.$q.loading.hide()
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },
        finishTest() {
            let error = false
            let scroll = ''
            var options = {
                container: '.q-modal-layout-content',
                easing: 'ease-in',
                force: true,
                cancelable: false,
                offset: -10
            }
            this.questions.forEach(question => {
                if (!question.answer) {
                    question.error = true
                    error = true
                    if (!scroll) scroll = question.id
                }
                else question.error = false
            })
            if (!error) {
                this.$q.loading.show()
                let answer = { answers: [] }
                this.questions.forEach(question => {
                    answer.answers.push({
                        id: question.id,
                        answer_id: question.answer
                    })
                })
                this.$axios.post(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/submit/`, answer, this.getHeader)
                .then(response => {
                    this.test_status = 'finished'
                    this.result = response.data
                    if (this.activity.type_slug === 'unevaluated_quiz') {
                        this.show_try_again = false
                    }
                    else if (!response.data.maximum_attempts && this.activity.type_activity != 'quiz' || (this.activity.type_slug === 'conventional_test' && response.data.percent != 100)) {
                        this.show_try_again = true
                    }
                    else if (response.data.qty_attempted < response.data.maximum_attempts && response.data.status != 1 && response.data.status != 4 && this.activity.type_activity != 'quiz') {
                        this.show_try_again = true
                    }
                    else {
                        this.show_try_again = false
                    }
                    this.$q.loading.hide()
                })
                .catch(e => {
                    this.$q.loading.hide()
                    console.log(e)
                })
            }
            else {
                this.$q.notify({
                    message: this.$t('test_need_check_all_answers'),
                    type: 'negative',
                    icon: 'fas fa-ban'
                })
                this.$scrollTo('#qt' + scroll, 500, options)
            }
        },
        tryAgain() {
            this.test_status = 'not_started'
            this.result = []
            this.startTest()
        },
        showDanger() {
            if (this.activity.type_slug != 'conventional_test' && this.activity.type_slug != 'evaluated_quiz' && this.activity.type_slug != 'unevaluated_quiz') {
                let quantity_left = this.activity.qty_attempted - this.activity.maximum_attempts
                if (quantity_left === 1) return true
                else return false
            }
        },
        getQuestionClass(index, question) {
            let return_class = ''
            if (index != 0) return_class += 'q-mt-md'
            if (question.error) return_class += ' text-negative'
            return return_class
        },
        getResult(status) {
            switch(status) {
                case 1: return 'aproved'
                break
                case 2: return 'reproved'
                break
                case 3: return 'blocked'
                break
                case 4: return 'restarted_training'
                break
                case 50: return 'improved_score'
                break
                case 60: return 'not_improved_score'
                break
            }
        },
        getResultClass(status) {
            switch(status) {
                case 1:
                case 50: return 'text-positive'
                break
                case 2:
                case 3: return 'text-negative'
                break
                case 4: return 'text-warning'
                break
                case 60: return ''
                break
            }
        },
        closeTest() {
            this.test_status = 'not_started'
            this.result = []
            this.$emit('clearTest')
        },
        showModalResult(result) {
            this.$q.loading.show()
            this.realization_questions = []
            this.$axios.get(`/etraining_courses/group_training/${this.$route.params.id}/group_training_test/${this.activity.type_id}/group_training_test_user_result/${result.id}/`, this.getHeader)
            .then(response => {
                response.data.questions.forEach(question => {
                    let answers = []
                    question.group_training_test_question_answers.forEach(answer => {
                        answers.push({ label: answer.title, value: answer.id })
                    })
                    this.realization_questions.push({
                        id: question.id,
                        title: question.title,
                        answers: answers,
                        answer: question.user_answer_id,
                        is_correct: question.is_correct
                    })
                })
                this.show_modal = true
                this.$q.loading.hide()
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },
        format(date_raw) {
            return date.formatDate(date_raw, 'DD/MM/YYYY HH:mm:ss')
        }
    },
    watch: {
        closing: function(val) {
            if (val == true) {
                this.test_status = 'not_started'
                this.result = []
                this.$emit('clearTest')
            }
        }
    },
    created() {
        this.selected_client_params = this.clients.find((obj) => { return obj.id === this.id_user_client_selected })
        this.id_course = this.$route.params.id
        if (this.has_active_test) this.getQuestions()
        this.realizations = []
        this.activity.user_test_results.forEach(result => {
            this.realizations.push(result)
        })
    }
}
</script>

<style lang="stylus" scoped>
</style>
